import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	ColorPicker,
	isReadable,
	useAutoTransitioningColors,
	useBuiltInColorMap,
} from '@atlassian/color-picker';
import { CornerStyle, useProductDependencies } from '@atlassian/company-hub-common';
import { CornerStyleConfig } from '@atlassian/company-hub-common/config-panel';

import type { SpotlightActions, SpotlightParameters } from '../../../../../../types';

import { i18n } from './messages';

const styles = cssMap({
	generalOptionStyles: {
		width: '100%',
	},
});

export type GeneralOptionProps = {
	actions: SpotlightActions;
	parameters: SpotlightParameters;
};

// TODO: COMPHUB-3164 - replace this background color field with the reusable one once it's implemented. Refer to ticket for more context
export const GeneralOptions = ({ parameters, actions }: GeneralOptionProps): React.JSX.Element => {
	const { isSSRProductInDarkMode, isCompanyHub, branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { formatMessage } = useIntl();
	const { backgroundColor, content, cornerStyle } = parameters ?? {};
	const { updateContentTextColor, updateBackgroundColor, updateCornerStyle } = actions;
	const { backgroundColorMap, emptyColorMap, textColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);
	const textColor = content?.text?.textColor;
	const isReadableBackgroundColor = (color: string) => isReadable(color, textColor);
	const isReadableTextColor = (color: string) => isReadable(color, backgroundColor);

	useAutoTransitioningColors(
		backgroundColor,
		isReadableTextColor,
		textColor,
		updateContentTextColor,
		branding?.defaultParameters,
	);

	return (
		<Flex testId="general-options" direction="column" xcss={cx(styles.generalOptionStyles)}>
			<Stack space="space.200">
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text">
						<FormattedMessage {...i18n.backgroundColor} />
					</Text>
					<ColorPicker
						builtInColorMap={
							hideDefaultColorPalette && brandBackgroundColorMap
								? brandBackgroundColorMap
								: backgroundColorMap
						}
						isReadableColor={isReadableBackgroundColor}
						onChange={updateBackgroundColor}
						value={backgroundColor}
						pickerTitle={i18n.backgroundColor}
						validationSuccessMessage={formatMessage(i18n.colorValidationSuccessMessage)}
						validationFailMessage={formatMessage(i18n.colorValidationFailMessage)}
						showCustomPalette={isCompanyHub}
						analyticsSource="spotlightExtensionConfigBackgroundColor"
					/>
				</Inline>
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text">
						<FormattedMessage {...i18n.textColor} />
					</Text>
					<ColorPicker
						builtInColorMap={hideDefaultColorPalette ? emptyColorMap : textColorMap}
						isReadableColor={isReadableTextColor}
						onChange={updateContentTextColor}
						value={textColor}
						pickerTitle={i18n.textColor}
						validationSuccessMessage={formatMessage(i18n.colorValidationSuccessMessage)}
						validationFailMessage={formatMessage(i18n.colorValidationFailMessage)}
						showCustomPalette={isCompanyHub}
						analyticsSource="spotlightExtensionConfigTextColor"
						showTextIcon
					/>
				</Inline>
				{isCompanyHub && (
					<CornerStyleConfig<CornerStyle>
						cornerStyleValue={cornerStyle}
						updateCornerStyle={updateCornerStyle}
						roundCornerValue={CornerStyle.ROUNDED}
						squareCornerValue={CornerStyle.SQUARED}
					/>
				)}
			</Stack>
		</Flex>
	);
};
